import { useMemo } from 'react';
import { useApiQuery } from './useApiQuery';
import { filesApi } from '@/api/files';
import type { FileStats, FileRecord, StorageUsage } from '@/api/files';
import mockData from '@/data/mock-data.json';

export const useFileStats = () => {
  const mockStats = useMemo<FileStats>(() => {
    const files = mockData.files as FileRecord[];
    const active = files.filter((f) => !f.deleted);
    return {
      all: active.length,
      sent: active.filter((f) => f.direction === 'sent').length,
      received: active.filter((f) => f.direction === 'received').length,
      pending: active.filter((f) => f.status === 'pending').length,
      starred: active.filter((f) => f.starred).length,
      deleted: files.filter((f) => f.deleted).length,
    };
  }, []);

  const mockStorage = useMemo<StorageUsage>(() => {
    const files = mockData.files as FileRecord[];
    const usedBytes = files
      .filter((f) => !f.deleted)
      .reduce((total, f) => total + (f.size ?? 0), 0);
    return { usedBytes, quotaBytes: 50 * 1024 ** 3 };
  }, []);

  const {
    data,
    isLoading: statsLoading,
    error: statsError,
    isUsingMockData: statsMock,
  } = useApiQuery<FileStats>({
    queryFn: () => filesApi.getFileStats(),
    mockData: mockStats,
  });

  const {
    data: storageData,
    isLoading: storageLoading,
    error: storageError,
    isUsingMockData: storageMock,
  } = useApiQuery<StorageUsage>({
    queryFn: () => filesApi.getStorageUsage(),
    mockData: mockStorage,
  });

  const emptyStats: FileStats = {
    all: 0,
    sent: 0,
    received: 0,
    pending: 0,
    starred: 0,
    deleted: 0,
  };
  const emptyStorage: StorageUsage = { usedBytes: 0, quotaBytes: 0 };

  return {
    // useApiQuery only yields mockData in local dev; in deployed envs a failure
    // leaves data undefined, so fall back to empty values (never mock) and let
    // consumers surface `error` instead of silently showing fake numbers.
    stats: data ?? emptyStats,
    storage: storageData ?? emptyStorage,
    isLoading: statsLoading || storageLoading,
    error: statsError ?? storageError,
    isUsingMockData: statsMock || storageMock,
  };
};
